export interface DocumentsAndMessagesKeyArguments {
  approvalId: string;
}

/**
 * Hierarchical query-key factory for the aggregated documents & messages read.
 * The shape mirrors `dscWorkbenchKeys` so cache invalidation stays predictable:
 * invalidating `all` flushes every documents-and-messages query, `details()`
 * flushes all reads, and `detail({ approvalId })` flushes a single deal's
 * documents & messages. Never write a literal key array outside this module —
 * the factory IS the schema.
 *
 * Keyed on approvalId alone: the BFF endpoint is
 * `/api/v1/workbench/applications/{appId}/docs-messages`, and the app feeds the
 * route `approvalId` as that `appId` (same identifier used by the sibling
 * `applications/{…}` status and VLA-lock endpoints).
 */
export const documentsAndMessagesKeys = {
  all: ["documentsAndMessages"] as const,
  details: () => [...documentsAndMessagesKeys.all, "detail"] as const,
  detail: (arguments_: DocumentsAndMessagesKeyArguments) =>
    [...documentsAndMessagesKeys.details(), arguments_] as const,
};

export interface DocumentContentUrlKeyArguments {
  appId: string;
  docId: string;
}

/**
 * Query-key factory for a single document's presigned content URL (OR-24135).
 * Kept in the same module as the docs-messages keys since both hang off the
 * documents domain. Keyed on `appId` + `docId` — the identifiers the viewer
 * window reads from its own URL and resolves a fresh content URL from.
 */
export const documentContentUrlKeys = {
  all: ["docContentUrl"] as const,
  detail: (arguments_: DocumentContentUrlKeyArguments) => [...documentContentUrlKeys.all, arguments_] as const,
};
